import { MediaThumbnail } from '@components/MediaThumbnail/MediaThumbnail';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { useVirtualScroll } from '@lib/hooks/useVirtualScroll';
import { showToast } from '@lib/utils/toastmaster';
import { formatDurationToHHMMSS } from '@lib/utils/units';
import { useMediaStatusPolling } from '@pages/Library/hooks/useMediaStatusPolling';
import {
  canOpenMediaModal,
  getDisplayProgressOrZero,
  getMediaDuration,
  isProbablyImage,
  isProcessingWithoutVideo,
  isProcessingWithVideo,
  isRetrieving,
} from '@pages/Library/utils/mediaStatus';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import React, { useCallback, useId } from 'react';
import { EmptyFolder, EmptyLibrary } from '../EmptyLibrary/EmptyLibrary';
import { MediaFolderLocation } from '../Folders/MediaFolderLocation';
import { MediaTableRow } from '../LibraryContentMediaTable/cells/types';
import { useMediaData } from '../LibraryContentMediaTable/hooks/useMediaData';
import { useModalHandlers } from '../LibraryContentMediaTable/hooks/useModalHandlers';
import { VideoMenuButton } from '../MediaCard/components/VideoMenuButton';
import { MediaListSkeleton } from '../MediaListSkeleton/MediaListSkeleton';
import css from './MobileMediaTableList.module.scss';

const ROW_HEIGHT = 73; // row height including border

const CircleProgress = ({ progress }: { progress: number }) => {
  const gradientId = useId();
  const size = 24;
  const radius = size / 2;
  const clamped = Math.min(Math.max(progress, 0), 100) / 100;
  const angle = clamped * 2 * Math.PI;
  const largeArc = clamped > 0.5 ? 1 : 0;
  const x = radius + radius * Math.sin(angle);
  const y = radius - radius * Math.cos(angle);

  return (
    <svg width={size} height={size} className={css.circleProgress}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(208, 169, 239, 1)" />
          <stop offset="100%" stopColor="rgba(157, 78, 221, 1)" />
        </linearGradient>
      </defs>
      <circle cx={radius} cy={radius} r={radius} fill="rgba(255, 255, 255, 0.08)" />
      {clamped > 0 && clamped < 1 && (
        <path
          d={`M${radius},${radius} L${radius},0 A${radius},${radius} 0 ${largeArc},1 ${x},${y} Z`}
          fill={`url(#${gradientId})`}
        />
      )}
      {clamped >= 1 && <circle cx={radius} cy={radius} r={radius} fill={`url(#${gradientId})`} />}
    </svg>
  );
};

const ErrorCircle = () => {
  const size = 24;
  const r = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={css.circleProgress}>
      <circle cx={r} cy={r} r={r} fill="rgba(214, 69, 80, 0.8)" />
      <line x1="8" y1="8" x2="16" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <line x1="16" y1="8" x2="8" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

function isMediaProcessing(media: MediaTableRow): boolean {
  if (media.isUploading) return true;
  return isRetrieving(media) || isProcessingWithVideo(media) || isProcessingWithoutVideo(media);
}

function isFileUpload(media: MediaTableRow): boolean {
  if (media.uploadStatus?.file) return true;
  return media.origin?.source === 'user';
}

function getCombinedProgress(media: MediaTableRow, liveUploadProgress: number): number {
  if (!isFileUpload(media)) {
    return getDisplayProgressOrZero(media);
  }
  if (media.isUploading) {
    return liveUploadProgress * 0.5;
  }
  return 50 + getDisplayProgressOrZero(media) * 0.5;
}

const MobileMediaRow = observer(function MobileMediaRow({
  media,
  isModifiable,
  onRowClick,
}: {
  media: MediaTableRow;
  isModifiable: boolean;
  onRowClick: (media: MediaTableRow, e: React.MouseEvent) => void;
}) {
  const uploadManager = useUploadManager();
  const title = media.isUploading ? (media as any).filename : media.metadata?.name || 'Unknown';
  const duration = isProbablyImage(media) ? undefined : getMediaDuration(media);
  const processing = isMediaProcessing(media);
  const errored = media.status === 'errored';

  const uploadUuid = uploadManager.mediaIdToLocalUUID[media._id] ?? media._id;
  const liveUploadProgress = uploadManager.uploadStatus[uploadUuid]?.progress ?? 0;
  const progress = processing ? getCombinedProgress(media, liveUploadProgress) : 0;

  const menuIcon = errored ? (
    <ErrorCircle />
  ) : processing ? (
    <CircleProgress progress={progress} />
  ) : undefined;

  return (
    <div className={css.row} onClick={(e) => onRowClick(media, e)}>
      <div className={css.thumbnailWrapper}>
        <MediaThumbnail media={media} className={css.thumbnail} />
        {duration != null && duration > 0 && (
          <span className={css.duration}>{formatDurationToHHMMSS(duration)}</span>
        )}
      </div>
      <div className={css.titleWrapper}>
        <span className={errored ? css.titleErrored : processing ? css.titleProcessing : css.title}>
          {title}
        </span>
        {!media.isUploading && <MediaFolderLocation media={media} className={css.folderLocation} />}
      </div>
      <div className={css.menuWrapper} onClick={(e) => e.stopPropagation()}>
        {media.isUploading ? (
          <div className={css.uploadingCircle}>
            <CircleProgress progress={progress} />
          </div>
        ) : (
          <VideoMenuButton
            media={media}
            isModifiable={isModifiable}
            iconVariant="vertical"
            customIcon={menuIcon}
          />
        )}
      </div>
    </div>
  );
});

export const MobileMediaTableList = observer(() => {
  const folder = useLibraryFolder();
  const { data: rawMediaData, isLoading } = useMediaData();

  // useMediaData already applies the omnifilter (shared with the grid and desktop
  // table), so the mobile list renders its rows directly. (Per-column filters are a
  // desktop DataTable feature and don't apply on mobile.)
  const mediaData = rawMediaData;
  const { handleThumbnailClick } = useModalHandlers();
  const isModifiable = !folder?.library?.isReadOnly;
  const { scrollRef, visibleRange, totalHeight, topPad, bottomPad, clampedEnd } = useVirtualScroll(
    mediaData.length,
    ROW_HEIGHT,
  );
  // Poll processing/uploading media for status updates. Folder-level, so the
  // visible range no longer narrows anything.
  useMediaStatusPolling();

  const handleRowClick = useCallback(
    (media: MediaTableRow, e: React.MouseEvent) => {
      if (media.isUploading) return;
      if (!canOpenMediaModal(media)) {
        showToast('media-modal-not-ready', { media });
        return;
      }
      handleThumbnailClick(media, e);
    },
    [handleThumbnailClick],
  );

  if (!folder) return null;

  const isEmpty = !isLoading && mediaData.length === 0;

  return (
    <GradientScrollDiv
      className={css.container}
      innerDivClassName={css.scrollInner}
      ref={scrollRef}
    >
      {folder.data === null ? (
        <MediaListSkeleton variant="compact" />
      ) : isEmpty ? (
        <div className={css.emptyContainer}>
          {folder.showsEmptyLibrary ? (
            <EmptyLibrary library={folder.library} />
          ) : (
            <EmptyFolder library={folder.library} />
          )}
        </div>
      ) : (
        <div className={css.list} style={{ minHeight: totalHeight }}>
          {topPad > 0 && <div style={{ height: topPad, flexShrink: 0 }} />}
          {mediaData.slice(visibleRange.start, clampedEnd).map((media) => (
            <MobileMediaRow
              key={media._id}
              media={media}
              isModifiable={isModifiable}
              onRowClick={handleRowClick}
            />
          ))}
          {bottomPad > 0 && <div style={{ height: bottomPad, flexShrink: 0 }} />}
        </div>
      )}
    </GradientScrollDiv>
  );
});
